import React, {useLayoutEffect, useRef} from 'react';
import {useTranslation} from 'react-i18next';
import {
  Dialog,
  DialogContent,
  Button,
  DialogFooter,
  ThemeName,
  Theme,
  Icon,
  InfoIcon,
  LoadingLosenge,
} from '@taopix/taopix-design-system';
import {useUiState} from '../../Context/UserInterface/UserInterfaceContext';
import {UserInterfaceActions} from '../../Context/UserInterface/UserInterfaceActionTypes';

export type EditPictureDialogProps = {
  isOpen: boolean;
  selectedAssetId?: number | '';
};

const EditPictureDialog = ({isOpen, selectedAssetId}: EditPictureDialogProps) => {
  const {t} = useTranslation(['*', 'dialogs']);
  const ui = useUiState();

  const calendarManager = window.Taopix.Core.get('calendarManager');
  const previewRef = useRef<HTMLDivElement>(null);

  useLayoutEffect(() => {
    const loadImageEditor = () => {
      if (previewRef.current) calendarManager.initCellImageEditor(previewRef.current).then();
    };
    if (isOpen && previewRef.current) {
      // Delay the loading of the image editor to avoid the context menu being positioned incorrectly due to dialog animation
      // Removed the timeout as it causes a flash when loading the cell image editor. Context menu seems to postion fine but is something to monitor.
      // setTimeout(loadImageEditor, 500);
      loadImageEditor();
    }
  }, [isOpen]);

  if (!isOpen) return <></>;

  return (
    <Dialog
      id={'edit-picture-dialog'}
      open={isOpen}
      className={'min-h-min min-w-min'}
      theme={'tpx-theme-dialogs'}
      themeTip="Dialogs"
      width={600}
      height={600}
      title={t('dialogs:str_TitleEditPicture', {defaultValue: 'Edit Picture'})}
      showTitle={false}
    >
      <DialogContent>
        <Theme
          name={ThemeName.Container}
          className={'flex flex-col items-center justify-center w-full h-full space-y-sm'}
        >
          <div
            className={
              'flex items-center justify-center max-w-[calc(100%-40px)] h-[calc(100%-140px)] aspect-square'
            }
            id={'edit-image-preview'}
            ref={previewRef}
          >
            <LoadingLosenge />
          </div>
        </Theme>
        <Theme name={ThemeName.Information} className={'flex items-center p-sm mt-sm self-center'}>
          <Icon icon={<InfoIcon />} className="mr-xs" />
          <p>{t('*:str_MessageUseZoomButtonsOrDragToChangeCrop')}</p>
        </Theme>
      </DialogContent>
      <DialogFooter isSmallScreen={ui.state.isSmallScreen}>
        <Button
          buttonStyle={'secondary'}
          label={t('*:str_ButtonClose')}
          size={'medium'}
          onClick={() => {
            ui.dispatch(UserInterfaceActions.setCalendarEditPictureDialog(false));
          }}
        />
        <Button
          buttonStyle={'primary'}
          label={t('*:str_ButtonSave')}
          size={'medium'}
          onClick={() => {
            // updateCellImage - if change made in large preview call to save
            calendarManager.updateCellImage().then(() => {
              ui.dispatch(UserInterfaceActions.setCalendarEditPictureDialog(false));
            });
          }}
        />
      </DialogFooter>
    </Dialog>
  );
};

export default EditPictureDialog;
